import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TextareaAutosize } from "@/components/ui/textarea-autosize";
import { Button } from "@/components/ui/button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CreateQuestion, updateQuestionStatus } from "./actions";
import { useModal } from "@/stores/use-modal-store";
import { Text } from "@/components/ui/text";
import { Checkbox } from "@/components/ui/checkbox";
import { useEventQnASettings } from "@/hooks/use-qna-query";

const validationSchema = Yup.object({
  content: Yup.string()
    .required("Question content is required")
    .max(250, "Question content cannot exceed 250 characters")
    .test("not-empty", "Question content cannot be empty or just spaces", (value) => {
      return value?.trim().length > 0;
    }),
  isAnonymous: Yup.boolean().nullable()
});

const PostQuestion = () => {
  const { isOpen, onClose, type, data } = useModal();
  const { eventId, question, callback } = data;
  const { data: qnaSettings } = useEventQnASettings({ eventId: eventId || "" });
  const isEditing = Boolean(question?._id);

  const formik = useFormik({
    initialValues: {
      content: isEditing ? question?.content : "",
      isAnonymous: false
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const { content, isAnonymous } = values;

      if (isEditing) {
        await updateQuestionStatus(eventId || "", question?._id || "", { content, isAnonymous });
      } else {
        await CreateQuestion(eventId || "", { content, isAnonymous });
      }
      callback && callback();
      resetForm();
      onClose();
    }
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[610px] md:max-h-[600px] max-w-full-50px w-[500px] overflow-y-auto">
        <DialogHeader className="sticky">
          <DialogTitle className="text-left">
            {isEditing ? "Edit question" : "Ask a question"}
          </DialogTitle>
        </DialogHeader>

        <TextareaAutosize
          placeholder="Submit a question"
          value={formik.values.content}
          name="content"
          onChange={formik.handleChange}
          maxLength={250}
          textareaClassName="field-border !w-[100%]"
          error={(formik.touched.content && formik.errors.content) as boolean}
          errorMessage={formik.errors.content}
          errorOutline={(formik.touched.content && formik.errors.content) as boolean}
        />

        {qnaSettings?.qnaSettings?.allowAnonymousQuestions && !isEditing && (
          <div className="flex items-center gap-1">
            <Checkbox
              size="small"
              checked={formik.values.isAnonymous}
              name="isAnonymous"
              onCheckedChange={(e) => formik.setFieldValue("isAnonymous", e)}
              className="border-black"
            />
            <Text variant="body_one">Submit your question anonymously</Text>
          </div>
        )}

        <div className="flex justify-end gap-m mt-m">
          <Button
            className="w-1/3"
            variant="primary"
            onClick={() => formik.handleSubmit()}
            disabled={
              formik.isSubmitting || (isEditing && formik.values.content === question?.content)
            }
            loading={formik.isSubmitting}
          >
            {isEditing ? "Update" : "Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostQuestion;
